import React from 'react';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Select from '@material-ui/core/Select';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import FormControl from '@material-ui/core/FormControl';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import CancelIcon from '@material-ui/icons/Cancel';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import { navigate } from '@reach/router';
import { useTranslation } from 'react-i18next';
import { Layout } from '~/Layout';
import getCrumbs from '~/Layout/crumbs';
import WeightCalculator, { getUnits } from '~/Summary/WeightCalculator';
import TYPES_OF_WASTE from '~/TypeOfWaste/typesOfWaste';


const toNumber = (value, unitSystem) => {
  const str = String(value);
  const cleaned = unitSystem === 'metric' ? str.replace(',', '.') : str.replace(/,/g, '');
  return parseFloat(cleaned);
};

const MIN_AMOUNT = 0.005;

const MIN_COEFFICIENT_TYPE = TYPES_OF_WASTE.reduce(
  (min, t) => (t.coefficient < min.coefficient ? t : min)
).slug;

const getConvertedWeight = (value, unitsValue, system) => {
  try {
    const calc = new WeightCalculator({
      quantity: { type: 'exact', units: unitsValue, amount: value },
      type: MIN_COEFFICIENT_TYPE, 
    });
    if (calc.isVolumetric()) {
      return calc.getWeight(system);
    }
    return system === 'imperial' ? calc.getWeightInLbs() : calc.getWeightInKg();
  } catch (e) {
    return null;
  }
};

const CssTextField = withStyles(theme => ({
  root: {
    '& .MuiInput-root': {
      paddingTop: '10px',
      paddingBottom: '10px',
    },
    '& label': {
      fontWeight: 'bold',
      color: theme.palette.primary.main,
      fontSize: 20,
    },
    '& label.Mui-focused': {
      fontSize: 20,
    },
    '& .MuiInput-underline:before': {
      borderBottomColor: theme.palette.primary.main,
      borderWidth: 3,
    },
    '& .MuiInput-underline:after': {
      borderWidth: 3,
    },
  },
}))(TextField);

const useStyles = makeStyles(theme => ({
  container: {
    overflow: 'visible',
    height: '100%',
    paddingLeft: 30,
    paddingRight: 30,
    paddingTop: 20,
  },
  form: {
    overflow: 'hidden',
    marginTop: 35,
  },
  selectLabel: {
    fontWeight: 'bold',
    color: '#111',
    fontSize: 20,
  },
  submit: {
    minHeight: 65,
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 'auto',
      paddingRight: 30,
    },
  },
  continueIcon: {
    [theme.breakpoints.up('sm')]: {
      position: 'absolute',
      right: 0,
    },
  },
  submitContainer: {
    [theme.breakpoints.up('sm')]: {
      position: 'absolute',
      bottom: 20,
      right: 0,
    },
  },
  menuItem: {
    fontSize: 20,
  },
  formControl: {
    width: 120,
  },
  amountField: {
    width: 120,
    '&::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
  },
  cancelIcon: {
    padding: 0,
    marginTop: 11,
  },
  disabledSelect: {
    '&.Mui-disabled:before': {
      borderBottomStyle: 'solid',
    },
  },
}));

const selectStyles = makeStyles({
  root: {
    fontSize: '2rem',
    paddingTop: '10px',
    paddingBottom: '10px',

    '&:before': {
      borderBottomColor: '#111',
      borderWidth: 3,
    },
    '&:after': {
      borderWidth: 3,
    },
  },
}, { name: 'MuiInput' });

const unitStyles = makeStyles({
  formControl: {
    transform: 'scale(1) translateY(40px)',
  },
  shrink: {
    transform: 'translate(0, 1.5px) scale(0.75)',
  },
}, { name: 'MuiInputLabel' });

const ExactQuantity = ({ quantity, selectQuantity, appSettings,  showAppMessage,
}) => {
  const { t } = useTranslation('exact_quantity_page');
  const { amount, units } = quantity;
  const { unitSystem } = appSettings
  const classes = useStyles();

  const isEntityLocked = String(appSettings.entityId) === "7";

  // override some global styles
  selectStyles();
  unitStyles();

  const validationSchema = Yup.object().shape({
    amount: Yup.string()
      .required('Amount is required')
      .test('is-number', 'Amount must be a number', v => !Number.isNaN(toNumber(v, unitSystem))),
    units: Yup.string().required(),
  });

  const handleSubmit = (values, { setSubmitting }) => {
    const num = toNumber(values.amount, unitSystem);

    if (isNaN(num) || num <= 0) {
      showAppMessage({
        variant: 'error',
        duration: 2000,
        message: t('negative_value'),
      });
      setSubmitting(false);
      return;
    }

    const converted = getConvertedWeight(num, values.units, unitSystem);
    const effective = converted !== null ? converted : num;

    if (effective < MIN_AMOUNT) {
      showAppMessage({
        variant: 'error',
        duration: 2000,
        message: t('larger_value'),
      });
      setSubmitting(false);
      return;
    }

    if (unitSystem === 'metric' && values.amount.includes(',')) {
      showAppMessage({
        variant: 'warning',
        duration: 4000,
        message: t('comma_warning'),
      });
    }

    selectQuantity({
      type: 'exact',
      amount: num,
      units: values.units,
    });
    navigate('/typeOfWaste');
  };

  return (
    <Layout crumbs={getCrumbs('home/kindOfWaste/whyTossed/destination/container/exactQuantity')} previousScreen="/container">
      <Box className={classes.container}>
        <Typography variant="h2" align="center">{t('title')}</Typography>
        <Formik
          initialValues={{
            amount: amount ? String(amount) : '',
            units: isEntityLocked ? 'kg' : (units || ''),
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
          }) => {
       
            const handleAmountChange = e => {
              const raw = e.target.value;
              let v = raw.replace(/(?!^-)[^\d.,]/g, '');

              if (unitSystem === 'metric') {
                const separatorIndex = v.search(/[.,]/);
                if (separatorIndex !== -1) {
                  v = v.slice(0, separatorIndex + 1)
                    + v.slice(separatorIndex + 1).replace(/[.,]/g, '');
                }
              } else {
                const dotIndex = v.indexOf('.');
                if (dotIndex !== -1) {
                  v = v.slice(0, dotIndex + 1)
                    + v.slice(dotIndex + 1).replace(/[.,]/g, '');
                }
              }

              setFieldValue('amount', v);
            };
            return(
            <Form noValidate className={classes.form}>
              <Grid container direction="column" spacing={10}>
                <Grid item>
                  <Grid container direction="row" justifyContent="center" alignItems="center" spacing={10}>
                    <Grid item>
                      <CssTextField
                        id="amount"
                         name="amount"
                        label={t('amount')}
                        type="text"
                        inputMode="decimal"
                        autoFocus
                        value={values.amount}
                        error={errors.amount && touched.amount}
                        onChange={handleAmountChange}
                        onBlur={handleBlur}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {values.amount && (
                                <IconButton
                                  classes={{ root: classes.cancelIcon }}
                                  aria-label="clear"
                                  onClick={() => setFieldValue('amount', '')}
                                >
                                  <CancelIcon />
                                </IconButton>
                              )}
                            </InputAdornment>
                          ),
                          classes: {
                            input: classes.amountField,
                          },
                        }}
                        inputProps={{
                          inputMode: 'decimal',
                          pattern: '[0-9]*[.,]?[0-9]*',
                        }}
                      />
                    </Grid>
                    <Grid item>
                      <FormControl
                        className={classes.formControl}
                      >
                        <InputLabel className={classes.selectLabel}>{t('units')}</InputLabel>
                        <Select
                          name="units"
                          value={isEntityLocked ? 'kg' : values.units}
                          disabled={isEntityLocked}
                          error={errors.units && touched.units}
                          onChange={handleChange}
                          className={isEntityLocked ? classes.disabledSelect : ''}
                          onBlur={handleBlur}
                          MenuProps={{
                            getContentAnchorEl: null,
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'left',
                            },
                          }}
                        >
                          {getUnits(unitSystem).map(unit => (
                            <MenuItem
                              value={unit}
                              key={unit}
                              classes={{ root: classes.menuItem }}
                            >
                              {t(`${unitSystem}.${unit}`)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item className={classes.submitContainer}>
                  <Box display="flex" flexDirection="row-reverse">
                    <Button type="submit" disabled={!values.amount || !values.units} className={classes.submit}>
                      {t('continue')}
                      <ChevronRightIcon className={classes.continueIcon} />
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}}
        </Formik>
      </Box>
    </Layout>
  );
};

ExactQuantity.propTypes = {
  quantity: PropTypes.shape({
    amount: PropTypes.number,
    units: PropTypes.string,
  }).isRequired,
  selectQuantity: PropTypes.func.isRequired,
  appSettings: PropTypes.shape({
    unitSystem: PropTypes.oneOf(['imperial', 'metric']),
  }).isRequired,
  showAppMessage: PropTypes.func.isRequired,
};

export default ExactQuantity;